import { QBankStructureLevelTag } from '@exam-management/components';
import { QBankStructureLevelCategory } from '@exam-management/graphql';
import {
  Box,
  Divider,
  ExpandMoreRoundedIcon,
  Flex,
  IconButton,
  Rotate,
  styled,
  TableCell,
  TableRow,
  Text,
  theme,
  type TableRowProps,
} from '@shared/components';

export const QBS_LEVEL_TAG_BG_COLOR_BY_CATEGORY = {
  [QBankStructureLevelCategory.Course]: theme.palette.grey[700],
  [QBankStructureLevelCategory.Part]: theme.palette.grey[700],
  [QBankStructureLevelCategory.Subject]: 'hsla(37, 96%, 59%, 1)',
  [QBankStructureLevelCategory.Group]: 'hsla(40, 97%, 65%, 1)',
  [QBankStructureLevelCategory.Chapter]: 'hsla(42, 97%, 77%, 1)',
  [QBankStructureLevelCategory.Topic]: 'hsla(43, 96%, 89%, 1)',
  [QBankStructureLevelCategory.SubTopic]: theme.palette.background.paper,
};

export interface DashboardTableRowProps extends TableRowProps {
  data: {
    id: string;
    levelName: string | undefined;
    order: number;
    parentId: string | null;
    level: QBankStructureLevelCategory;
    parentIds: string[];
    calculate?: {
      hardQuestions: number | null;
      mediumQuestions: number | null;
      easyQuestions: number | null;
    };
    nonCalculate?: {
      hardQuestions: number | null;
      mediumQuestions: number | null;
      easyQuestions: number | null;
    };
    total?: string;
  };
  expanded?: boolean;
  onExpand?: (id: string) => void;
  totalBoxes?: number;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderColor: theme.palette.grey[300],
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  ...theme.typography.body2Semibold,
}));

function renderBox(totalBoxes: number) {
  return (
    <>
      {Array.from({ length: totalBoxes }).map((_, index) => (
        <Box
          key={index}
          sx={{
            minWidth: '16px',
            borderRight: (theme) => `1px solid ${theme.palette.grey[300]}`,
          }}
        />
      ))}
    </>
  );
}

export default function DashboardTableRow(props: Readonly<DashboardTableRowProps>) {
  const { data, expanded: isExpanded = false, onExpand, ...restProps } = props;
  const { id, levelName, calculate, nonCalculate, level, total, order } = data;
  const expandedBgColor = QBS_LEVEL_TAG_BG_COLOR_BY_CATEGORY[level];

  const summary =
    (calculate?.easyQuestions ?? 0) +
    (calculate?.mediumQuestions ?? 0) +
    (calculate?.hardQuestions ?? 0) +
    (nonCalculate?.easyQuestions ?? 0) +
    (nonCalculate?.mediumQuestions ?? 0) +
    (nonCalculate?.hardQuestions ?? 0);

  let itemSetDisplay: string | number = '-';

  if (total) {
    itemSetDisplay = total;
  } else if (summary > 0) {
    itemSetDisplay = summary;
  }

  return (
    <TableRow
      {...restProps}
      sx={{
        border: 'none',
        ':last-child': {
          borderRight: 'none',
          td: {
            borderBottom: 'none',
          },
        },
        backgroundColor: isExpanded ? expandedBgColor : theme.palette.background.paper,
        ...restProps.sx,
      }}
    >
      <StyledTableCell
        sx={{
          p: (theme) => theme.spacing(0),
          borderBottom: 'none',
        }}
      >
        <Flex
          sx={{
            minHeight: '48px',
            backgroundColor: (theme) => theme.palette.background.paper,
            color:
              level === QBankStructureLevelCategory.Part && isExpanded
                ? theme.palette.common.white
                : undefined,
          }}
        >
          {renderBox(props.totalBoxes ?? 0)}
          <Flex
            sx={{
              alignItems: 'center',
              backgroundColor: isExpanded ? expandedBgColor : theme.palette.background.paper,
              flex: 1,
              borderBottom: '1px solid ' + theme.palette.grey[300],
            }}
          >
            <Flex sx={{ minWidth: '48px', alignItems: 'center', justifyContent: 'center' }}>
              {onExpand ? (
                <IconButton
                  onClick={() => onExpand?.(id)}
                  sx={{
                    color:
                      level === QBankStructureLevelCategory.Part && isExpanded
                        ? theme.palette.common.white
                        : undefined,
                  }}
                >
                  <Rotate in={isExpanded}>
                    <ExpandMoreRoundedIcon />
                  </Rotate>
                </IconButton>
              ) : null}
            </Flex>
            <Flex
              sx={(theme) => ({
                flex: 1,
                p: theme.spacing(2, 1),
                gap: theme.spacing(2),
              })}
            >
              <QBankStructureLevelTag category={level} />
              <Text>{order}</Text>
              <Divider orientation='vertical' flexItem />
              <Text>{levelName}</Text>
            </Flex>
          </Flex>
        </Flex>
      </StyledTableCell>
      <StyledTableCell
        sx={{
          color:
            level === QBankStructureLevelCategory.Part && isExpanded
              ? theme.palette.common.white
              : undefined,
        }}
        align='center'
      >
        {calculate?.easyQuestions ?? '-'}
      </StyledTableCell>
      <StyledTableCell
        sx={{
          color:
            level === QBankStructureLevelCategory.Part && isExpanded
              ? theme.palette.common.white
              : undefined,
        }}
        align='center'
      >
        {calculate?.mediumQuestions ?? '-'}
      </StyledTableCell>
      <StyledTableCell
        sx={{
          color:
            level === QBankStructureLevelCategory.Part && isExpanded
              ? theme.palette.common.white
              : undefined,
        }}
        align='center'
      >
        {calculate?.hardQuestions ?? '-'}
      </StyledTableCell>
      <StyledTableCell
        sx={{
          color:
            level === QBankStructureLevelCategory.Part && isExpanded
              ? theme.palette.common.white
              : undefined,
        }}
        align='center'
      >
        {nonCalculate?.easyQuestions ?? '-'}
      </StyledTableCell>
      <StyledTableCell
        sx={{
          color:
            level === QBankStructureLevelCategory.Part && isExpanded
              ? theme.palette.common.white
              : undefined,
        }}
        align='center'
      >
        {nonCalculate?.mediumQuestions ?? '-'}
      </StyledTableCell>
      <StyledTableCell
        sx={{
          color:
            level === QBankStructureLevelCategory.Part && isExpanded
              ? theme.palette.common.white
              : undefined,
        }}
        align='center'
      >
        {nonCalculate?.hardQuestions ?? '-'}
      </StyledTableCell>
      <StyledTableCell
        sx={{
          color:
            level === QBankStructureLevelCategory.Part && isExpanded
              ? theme.palette.common.white
              : undefined,
        }}
        align='center'
      >
        {itemSetDisplay}
      </StyledTableCell>
    </TableRow>
  );
}
